import { Group, Select, Stack, Text, TextInput } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import moment from 'moment';
import { useMemo, useState } from 'react';

import { EnvironmentCountries } from '@e-origin/shared';

import { DatePickerInput } from '@components';
import { useSettings } from '@hooks/useSettings';

interface InputConfig {
  field: string;
  label: string;
  disabled: boolean;
  type: 'input' | 'datepicker' | 'dropdown';
  options?: { value: string; label: string }[];
  style?: any;
}

interface SummaryTabProps {
  form: UseFormReturnType<any>;
  editDisabled: boolean;
  stateOfSealsRequired: boolean;
}

export const SummaryTab: React.FC<SummaryTabProps> = ({ form, editDisabled, stateOfSealsRequired }) => {
  const { data: settings } = useSettings();
  const [conformDisabled, setConformDisabled] = useState(true);

  const inputs = useMemo<InputConfig[]>(
    () => [
      {
        field: 'conform',
        label: 'Conform',
        disabled: conformDisabled,
        type: 'dropdown',
        options: [
          { value: '0', label: 'No' },
          { value: '1', label: 'Yes' },
        ],
        style: conformDisabled ? null : { border: '1px solid blue' },
      },
      {
        field: 'unloadingCompletion',
        label: 'Unloading completion',
        disabled: editDisabled || settings.country !== EnvironmentCountries.NL,
        type: 'dropdown',
        options: [
          { value: '0', label: 'No' },
          { value: '1', label: 'Yes' },
        ],
      },
      {
        field: 'unloadingDate',
        label: 'Unloading date',
        disabled: editDisabled,
        type: 'datepicker',
      },
      {
        field: 'stateOfSeals',
        label: 'State of seals',
        disabled: editDisabled || !stateOfSealsRequired,
        type: 'dropdown',
        options: [
          { value: '', label: '' },
          { value: '0', label: '0 - NOT OK (missing, damaged, discrepancies)' },
          { value: '1', label: '1 - OK' },
        ],
      },
      {
        field: 'unloadingRemark',
        label: 'Unloading remark',
        disabled: editDisabled,
        type: 'input',
      },
    ],
    [editDisabled, stateOfSealsRequired, conformDisabled],
  );

  const handleTextChange = (field: string, value: string) => {
    form.setFieldValue(`UnloadingRemark.${field}`, value);

    if (field === 'unloadingRemark') {
      form.setFieldValue(
        'UnloadingRemark.conform',
        Number(value?.length > 0 ? false : form.values.stateOfSeals !== '0' && !form.values.trace?.length).toString(),
      );
      setConformDisabled(value?.length === 0);
    }
  };

  const handleSelectChange = (field: string, value: string) => {
    form.setFieldValue(`UnloadingRemark.${field}`, value);

    if (field === 'stateOfSeals') {
      form.setFieldValue('UnloadingRemark.conform', Number(value !== '0' && !form.values.trace?.length).toString());
    }
  };

  const handleDateChange = (field: string, date: Date) => {
    form.setFieldValue(`UnloadingRemark.${field}`, `${moment(date).format('YYYY-MM-DD')}T00:00:00`);
  };

  return (
    <Stack gap="md">
      {inputs.map((input) => (
        <Group key={`RowUnloadingRemark.${input.field}.${editDisabled}`} justify="space-between" wrap="nowrap" w={500}>
          <Text size="sm" w={180}>
            {input.label}
          </Text>

          {input.type === 'input' && (
            <TextInput
              {...form.getInputProps(`UnloadingRemark.${input.field}`)}
              value={form.values.UnloadingRemark[input.field]}
              onChange={(event) => handleTextChange(input.field, event.target.value)}
              disabled={input.disabled}
              w={280}
              data-pw={`unloading-${input.field}-input`}
              style={input.style}
            />
          )}

          {input.type === 'datepicker' && (
            <DatePickerInput
              value={form.values.UnloadingRemark[input.field] ? new Date(form.values.UnloadingRemark[input.field]) : new Date()}
              onChange={(date) => handleDateChange(input.field, date)}
              disabled={input.disabled}
              clearable={false}
              w={280}
              data-pw={`unloading-${input.field}-datepicker`}
              style={input.style}
            />
          )}

          {input.type === 'dropdown' && (
            <Select
              data={input.options}
              value={form.values.UnloadingRemark[input.field]}
              onChange={(value) => handleSelectChange(input.field, value)}
              disabled={input.disabled}
              w={280}
              maxDropdownHeight={150}
              data-pw={`${input.field.toLowerCase()}-select`}
              style={input.style}
            />
          )}
        </Group>
      ))}
    </Stack>
  );
};
